var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper, Step, StepLabel, Stepper, useMediaQuery, Box, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/system';
import CheckIcon from '@mui/icons-material/Check';
var getStepState = function (index, activeStep) {
    if (index < activeStep)
        return 'done';
    if (index === activeStep)
        return 'active';
    return 'pending';
};
var getStateColor = function (state, theme) {
    if (state === 'active')
        return theme.palette.primary.main;
    if (state === 'done')
        return '#2e7d32'; // green (MUI-compatible)
    return '#bdbdbd'; // light gray for icon background when pending
};
var getStateText = function (state) {
    if (state === 'active')
        return 'a preencher';
    if (state === 'done')
        return 'preenchido';
    return 'pendente';
};
var getStatusColor = function (state, theme) {
    if (state === 'active')
        return theme.palette.primary.main;
    if (state === 'done')
        return (theme.palette.success && theme.palette.success.dark) || '#1b5e20';
    return '#9e9e9e'; // neutral gray for pending text
};
var StepIcon = function (_a) {
    var state = _a.state, isCompact = _a.isCompact, className = _a.className, color = _a.color;
    var isDone = state === 'done';
    var isActive = state === 'active';
    var size = isCompact ? 24 : 28;
    var bg = isDone || isActive ? color : '#bdbdbd';
    return (_jsx("span", __assign({ className: className, style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: size,
            height: size,
            borderRadius: '50%',
            background: bg,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
        }, "aria-hidden": "true" }, { children: isDone ? _jsx(CheckIcon, { sx: { fontSize: isCompact ? 16 : 18 } }) : null })));
};
export var DefaultHeaderSteps = function (_a) {
    var _b = _a.steps, steps = _b === void 0 ? [] : _b, _c = _a.activeStep, activeStep = _c === void 0 ? 0 : _c, onChange = _a.onChange, _d = _a.clickable, clickable = _d === void 0 ? false : _d;
    var theme = useTheme();
    var isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    var isCompact = useMediaQuery('(max-height: 700px)') || isMobile;
    var _e = useState(0), maxStep = _e[0], setMaxStep = _e[1];
    var _f = useState(isMobile ? 64 : 72), stickyTop = _f[0], setStickyTop = _f[1];
    var _g = useState(0), containerLeft = _g[0], setContainerLeft = _g[1];
    var _h = useState(0), containerWidth = _h[0], setContainerWidth = _h[1];
    var paperRef = useRef(null);
    useEffect(function () {
        var computeStickyTop = function () {
            var fallback = isMobile ? 64 : 72;
            var headerEl = document.getElementById('HEADER_NAME');
            var appBar = headerEl === null || headerEl === void 0 ? void 0 : headerEl.closest('.MuiAppBar-root');
            var rect = appBar === null || appBar === void 0 ? void 0 : appBar.getBoundingClientRect();
            var measuredTop = (rect === null || rect === void 0 ? void 0 : rect.height) ? Math.round(rect.height) : fallback;
            setStickyTop(measuredTop);
        };
        computeStickyTop();
        window.addEventListener('resize', computeStickyTop);
        return function () {
            window.removeEventListener('resize', computeStickyTop);
        };
    }, [isMobile]);
    useEffect(function () {
        var computeSelfHeight = function () {
            var _a;
            var h = (_a = paperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
            var px = h ? "".concat(Math.round(h), "px") : '0px';
            document.documentElement.style.setProperty('--steps-header-height', px);
        };
        computeSelfHeight();
        window.addEventListener('resize', computeSelfHeight);
        return function () {
            window.removeEventListener('resize', computeSelfHeight);
        };
    }, [steps === null || steps === void 0 ? void 0 : steps.length, isMobile]);
    useEffect(function () {
        document.documentElement.style.setProperty('--steps-top-spacing', '108px');
        return function () {
            document.documentElement.style.setProperty('--steps-top-spacing', '0px');
        };
    }, []);
    useEffect(function () {
        return function () {
            document.documentElement.style.setProperty('--steps-header-height', '0px');
        };
    }, []);
    useEffect(function () {
        if (activeStep > maxStep) {
            setMaxStep(activeStep);
        }
    }, [activeStep]);
    useEffect(function () {
        var computeContainerBounds = function () {
            var _a, _b, _c;
            var el = paperRef.current;
            var containerEl = document.querySelector('.MuiContainer-root');
            if (!containerEl && el) {
                containerEl = (_c = (_b = (_a = el.parentElement) === null || _a === void 0 ? void 0 : _a.closest('.MuiContainer-root')) !== null && _b !== void 0 ? _b : el.parentElement) !== null && _c !== void 0 ? _c : null;
            }
            if (!containerEl) {
                setContainerLeft(0);
                setContainerWidth(0);
                return;
            }
            var rect = containerEl.getBoundingClientRect();
            var style = window.getComputedStyle(containerEl);
            var padLeft = parseFloat(style.paddingLeft || '0') || 0;
            var padRight = parseFloat(style.paddingRight || '0') || 0;
            var innerLeft = Math.round(rect.left + padLeft);
            var innerWidth = Math.round(Math.max(0, rect.width - padLeft - padRight));
            setContainerLeft(innerLeft);
            setContainerWidth(innerWidth);
        };
        computeContainerBounds();
        window.addEventListener('resize', computeContainerBounds);
        return function () {
            window.removeEventListener('resize', computeContainerBounds);
        };
    }, [isMobile, steps === null || steps === void 0 ? void 0 : steps.length]);
    var handleClick = function (step) {
        if (clickable && step <= maxStep) {
            onChange === null || onChange === void 0 ? void 0 : onChange(step);
        }
    };
    var stepper = useMemo(function () {
        return (_jsx(Stepper, __assign({ orientation: 'horizontal', activeStep: activeStep, alternativeLabel: !isMobile, sx: __assign(__assign({ width: '100%', overflowX: isMobile ? 'auto' : 'visible', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }, (isMobile
                ? {
                    '& .MuiStep-root': {
                        minWidth: 96,
                        flex: '0 0 auto',
                        paddingLeft: 0,
                        paddingRight: 0,
                        alignSelf: 'flex-start',
                    },
                    '& .MuiStepLabel-root': {
                        width: '100%',
                        justifyContent: 'center',
                        textAlign: 'center',
                        alignItems: 'flex-start',
                    },
                    '& .MuiStepLabel-iconContainer': {
                        width: '100%',
                        justifyContent: 'center',
                        paddingRight: 0,
                        position: 'relative',
                        height: isCompact ? 28 : 32,
                        alignItems: 'flex-start',
                    },
                }
                : {})), { '& .MuiStepConnector-line': {
                    borderTopWidth: 2,
                    borderColor: '#d0d0d0',
                }, '& .MuiStepLabel-label': {
                    display: 'none',
                } }), nonLinear: true }, { children: steps === null || steps === void 0 ? void 0 : steps.map(function (label, index) {
                var state = getStepState(index, activeStep);
                var color = getStateColor(state, theme);
                var statusText = getStateText(state);
                var statusColor = getStatusColor(state, theme);
                var canClick = clickable && index <= maxStep;
                return (_jsxs(Step, { children: [_jsx(StepLabel, { onClick: function () { return handleClick(index); }, sx: {
                                cursor: canClick ? 'pointer' : 'default',
                                '& .MuiStepIcon-root': {
                                    color: color,
                                },
                                '& .MuiStepIcon-text': {
                                    display: 'none',
                                },
                            }, StepIconComponent: function (props) { return (_jsx(StepIcon, { state: state, isCompact: isCompact, className: props.className, color: color })); } }), _jsxs(Box, __assign({ onClick: canClick ? function () { return handleClick(index); } : undefined, sx: {
                                textAlign: 'center',
                                mt: isCompact ? 0.5 : 1,
                                cursor: canClick ? 'pointer' : 'default',
                                userSelect: 'none',
                                px: isMobile ? 0.5 : 0,
                                minHeight: isMobile ? 32 : 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                            } }, { children: [_jsx(Typography, __assign({ sx: { fontSize: isCompact ? 10 : 12, fontWeight: 700, color: color }, component: "div" }, { children: "PASSO - ".concat(index + 1) })), _jsx(Typography, __assign({ component: "div", sx: {
                                        fontSize: isCompact ? 10 : 12,
                                        fontWeight: 700,
                                        color: '#000',
                                        mt: 0.25,
                                        whiteSpace: isMobile ? 'normal' : 'normal',
                                        maxWidth: isMobile ? 100 : 'none',
                                        overflow: 'hidden',
                                        textOverflow: 'clip',
                                        display: isMobile ? '-webkit-box' : 'block',
                                        WebkitLineClamp: isMobile ? 2 : 'unset',
                                        WebkitBoxOrient: isMobile ? 'vertical' : undefined,
                                        lineHeight: 1.1,
                                    } }, { children: label })), !isCompact ? (_jsx(Typography, __assign({ sx: { fontSize: 11, fontWeight: 600, color: statusColor, mt: 0.5, textTransform: 'lowercase' }, component: "div" }, { children: statusText }))) : null] }))] }, "".concat(label, "-").concat(index.toString())));
            }) })));
    }, [activeStep, steps, isMobile, isCompact, clickable, maxStep, theme]);
    var fixedStyle = useMemo(function () {
        var left = containerWidth > 0 ? "".concat(containerLeft, "px") : '0px';
        var width = containerWidth > 0 ? "".concat(containerWidth, "px") : '100%';
        return { left: left, width: width };
    }, [containerLeft, containerWidth]);
    return (_jsx(Paper, __assign({ "data-testid": "default-header-steps", "data-clickable": String(clickable), ref: paperRef, sx: {
            position: 'sticky',
            top: 'var(--appbar-height, 96px)',
            left: '0',
            zIndex: '1000',
            width: '100%',
            padding: isCompact ? 1 : 3,
            isolation: 'isolate',
            contain: 'paint',
            willChange: 'transform',
            backgroundColor: '#fafafa',
            background: '#fafafa',
            opacity: 1,
            backgroundClip: 'padding-box',
            borderRadius: 0,
            boxShadow: 'none',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            backfaceVisibility: 'hidden',
            overflow: 'hidden',
        } }, { children: containerWidth > 0 ? (_jsx(Box, __assign({ sx: { width: fixedStyle.width, mx: 'auto' } }, { children: stepper }))) : (_jsx(Box, __assign({ sx: { maxWidth: 1152, margin: '0 auto', width: '100%' } }, { children: stepper }))) })));
};
